import React, { useEffect, useRef } from 'react';
import { Box, BoxProps, SxProps, Theme, useTheme } from '@mui/material';

interface GlassBackdropProps extends Omit<BoxProps, 'children'> {
  children: React.ReactNode;
}

export const GlassBackdrop: React.FC<GlassBackdropProps> = ({ children, sx, ...rest }) => {
  // The ambient gradient now lives on the body (see themeConfig MuiCssBaseline) so
  // it spans the whole viewport continuously — behind the glass sidebar, the
  // content gap, and the dashboard alike. GlassBackdrop stays a transparent
  // positioning wrapper so that single body gradient shows through with no seam
  // and no double-glow.
  return (
    <Box {...rest} sx={{ position: 'relative', ...(sx as object) }}>
      {children}
    </Box>
  );
};

interface GlassSurfaceProps {
  children: React.ReactNode;
  accentColor?: string;
  interactive?: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

export const GlassSurface: React.FC<GlassSurfaceProps> = ({
  children,
  accentColor,
  interactive = true,
  onClick,
  sx,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const ref = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  const glass = isDark
    ? {
        bg: 'rgba(255,255,255,0.08)',
        bgHover: 'rgba(255,255,255,0.14)',
        border: 'rgba(255,255,255,0.12)',
        topHl: 'rgba(255,255,255,0.18)',
        outerShadow: '0 4px 14px rgba(0,0,0,0.22)',
        outerShadowHover: '0 14px 30px rgba(0,0,0,0.32)',
        solidFallback: 'rgba(28,28,32,0.94)',
        neutralBorderHover: 'rgba(255,255,255,0.22)',
      }
    : {
        bg: 'rgba(255,255,255,0.75)',
        bgHover: 'rgba(255,255,255,0.88)',
        border: 'rgba(255,255,255,0.70)',
        topHl: 'rgba(255,255,255,0.95)',
        outerShadow: '0 4px 14px rgba(0,0,0,0.08)',
        outerShadowHover: '0 14px 26px rgba(0,0,0,0.14)',
        solidFallback: 'rgba(252,252,254,0.98)',
        neutralBorderHover: 'rgba(0,0,0,0.14)',
      };

  let borderHover = glass.neutralBorderHover;
  let cursorTint: string | null = null;
  if (accentColor && /^#[0-9a-fA-F]{6}$/.test(accentColor)) {
    const hex = accentColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    borderHover = `rgba(${r},${g},${b},${isDark ? 0.45 : 0.55})`;
    if (isDark) cursorTint = `rgba(${r},${g},${b},0.14)`;
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.style.setProperty('--mx', `${x}px`);
      ref.current.style.setProperty('--my', `${y}px`);
    });
  };

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  return (
    <Box
      ref={ref}
      onClick={onClick}
      onMouseMove={cursorTint ? handleMouseMove : undefined}
      sx={{
        position: 'relative',
        borderRadius: 3,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: glass.bg,
        backdropFilter: 'blur(14px) saturate(150%)',
        WebkitBackdropFilter: 'blur(14px) saturate(150%)',
        border: `1px solid ${glass.border}`,
        boxShadow: `inset 0 1px 0 ${glass.topHl}, ${glass.outerShadow}`,
        transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        '@supports not ((backdrop-filter: blur(14px)) or (-webkit-backdrop-filter: blur(14px)))': {
          backgroundColor: glass.solidFallback,
        },
        ...(cursorTint && {
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(520px circle at var(--mx, 50%) var(--my, 50%), ${cursorTint}, transparent 45%)`,
            opacity: 0,
            transition: 'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none',
            zIndex: 1,
          },
        }),
        ...(interactive && {
          '&:hover': {
            backgroundColor: glass.bgHover,
            borderColor: borderHover,
            backdropFilter: 'blur(18px) saturate(160%)',
            WebkitBackdropFilter: 'blur(18px) saturate(160%)',
            transform: 'translateY(-4px)',
            boxShadow: `inset 0 1px 0 ${glass.topHl}, ${glass.outerShadowHover}`,
            ...(cursorTint && { '&::before': { opacity: 1 } }),
          },
        }),
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'background-color 250ms, border-color 250ms, box-shadow 250ms',
          ...(cursorTint && { '&::before': { display: 'none' } }),
          ...(interactive && {
            '&:hover': {
              backgroundColor: glass.bgHover,
              borderColor: borderHover,
              transform: 'none',
              boxShadow: `inset 0 1px 0 ${glass.topHl}, ${glass.outerShadow}`,
            },
          }),
        },
        ...(sx as object),
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 2, height: '100%' }}>{children}</Box>
    </Box>
  );
};
